// Disconnect toast + TV static fallback.
// Shows a toast and overlays random noise static when
// the LiveSocket connection drops; reverses on reconnect.
// Noise texture is generated once and animated via CSS transforms
// (translate + rotate) for the jittering static effect — no per-frame
// pixel work.

import gsap from "gsap";
import { illuminateCssClassModal } from "../../.dox/ui";

const APP_SHELL = illuminateCssClassModal("appShell");

// ── Noise overlay ──────────────────────────────────────────────

const NOISE_ID = "lv-disconnect-noise";
const OVERSCAN = 1.8;

let jitterActive = false;

const buildNoiseCanvas = (): HTMLCanvasElement => {
  const size = Math.max(window.innerWidth, window.innerHeight);
  const tw = Math.ceil(size * OVERSCAN);

  const c = document.createElement("canvas");
  c.id = NOISE_ID;
  c.width = tw;
  c.height = tw;
  Object.assign(c.style, {
    position: "fixed",
    top: "50%",
    left: "50%",
    width: `${tw}px`,
    height: `${tw}px`,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: "1",
    opacity: "0",
  });

  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(tw, tw);
  const d = img.data;
  const len = tw * tw * 4;
  for (let i = 0; i < len; i += 4) {
    const v = (Math.random() * 256) | 0;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return c;
};

const ROTATIONS = [0, 90, 180, 270] as const;

const jitterTick = (): void => {
  const el = document.getElementById(NOISE_ID);
  if (!el || !jitterActive) return;
  const rot = ROTATIONS[(Math.random() * 4) | 0]!;
  gsap.set(el, {
    x: (Math.random() - 0.5) * 200,
    y: (Math.random() - 0.5) * 200,
    rotation: rot,
  });
};

// ── Disconnect fallback ────────────────────────────────────────

// Pure timeline builder: given shell + canvas targets, returns a
// configured gsap timeline. No DOM lookup, no module-state writes,
// no `.play()` — the caller decides when to start. Inputs in,
// timeline out. Tested in isolation in disconnect.test.ts without
// needing a noise canvas in JSDOM. The timeline composes three
// tweens: shell dissolve (0→120s), fast canvas fade-in (0→1s), slow
// canvas burn (1→120s). Easing / duration / position parameters are
// the only knobs of this animation; they are the contract this
// builder pins. Build/play are split so callers (and tests) can pin
// the timeline shape without committing to wall-clock progression.
export const buildFallbackTimeline = (
  shellEl: Element,
  canvasEl: Element,
): gsap.core.Timeline =>
  gsap
    .timeline({ paused: true })
    .to(
      shellEl,
      {
        filter: "blur(30px)",
        opacity: 0.15,
        y: 300,
        z: -500,
        rotationX: 35,
        duration: 120,
        ease: "power2.out",
      },
      0,
    )
    .to(canvasEl, { opacity: 0.05, duration: 1, ease: "power2.out" }, 0)
    .to(canvasEl, { opacity: 0.25, duration: 119, ease: "power2.in" }, 1);

const showDisconnectFallback = (): void => {
  const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);
  if (!shell) return;

  shell.style.pointerEvents = "none";
  shell.style.userSelect = "none";
  document.documentElement.style.overflow = "hidden";
  if (shell.parentElement) shell.parentElement.style.perspective = "3000px";

  // z-index 10 keeps the shell above the noise canvas (z-index 1)
  shell.style.zIndex = "10";

  // Static overlay — insert before the shell so it renders behind it
  // in the same perspective stacking context as the toast (z-[100])
  let canvas = document.getElementById(NOISE_ID) as HTMLCanvasElement | null;
  if (!canvas) {
    canvas = buildNoiseCanvas();
    shell.parentElement!.insertBefore(canvas, shell);
  }

  // Single timeline: shell dissolves while noise fades in behind it.
  // Build is pure (no side-effects beyond gsap's own bookkeeping);
  // play starts the wall clock. Splitting the two lets the unit
  // tests pin the timeline shape without committing to progression.
  const tl = buildFallbackTimeline(shell, canvas);
  tl.play();
  jitterActive = true;
  gsap.ticker.add(jitterTick);
};

const hideDisconnectFallback = (): void => {
  const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);
  if (!shell) return;

  gsap.killTweensOf(shell);
  shell.style.pointerEvents = "";
  shell.style.userSelect = "";
  shell.style.zIndex = "";
  document.documentElement.style.overflow = "";
  if (shell.parentElement) shell.parentElement.style.perspective = "";

  gsap.to(shell, {
    filter: "blur(0px)",
    opacity: 1,
    y: 0,
    z: 0,
    rotationX: 0,
    clearProps: "all",
    duration: 1.5,
    ease: "power2.out",
  });

  const canvas = document.getElementById(NOISE_ID);
  if (canvas) {
    jitterActive = false;
    gsap.ticker.remove(jitterTick);
    gsap.killTweensOf(canvas);
    canvas.remove();
  }
};

// ── Toast ──────────────────────────────────────────────────────

const DISCONNECT_ID = "lv-disconnect-toast";

// While the browser is unloading the page (user clicked a link / submitted a
// form), the LiveSocket will error/close as a matter of course. We don't want
// the dramatic disconnect fallback in that window — just spin the logo as a
// loader. Flipped on by `beforeunload` and cleared by `pageshow` (bfcache).
let unloading = false;

// Set by the `phx:server-draining` listener in `app.ts` when the server
// broadcasts that a graceful drain is underway (SIGTERM → Operation.Drain
// → ServerDrainHook on every connected LV). When the socket subsequently
// closes during the drain window we know the disconnect is *expected* —
// skip the dramatic overlay and just spin the logo as a "back in a sec"
// indicator. Cleared on the next successful reconnect (`onOpen` →
// `hideDisconnectToast`), so a future surprise disconnect still shows
// the full UI.
let expectedShutdown = false;
export const setExpectedShutdown = (on: boolean): void => {
  expectedShutdown = on;
  if (on) {
    spinLogo(true);
    spinFavicon(true);
  }
};
const spinLogo = (on: boolean): void => {
  for (const logo of Array.from(
    document.querySelectorAll<HTMLImageElement>('nav img[alt="Operation"]'),
  )) {
    if (on) {
      logo.style.transform = "";
      logo.classList.add("animate-spin");
    } else if (logo.classList.contains("animate-spin")) {
      // CSS transitions don't fire when an animation is removed — the
      // defined style hasn't changed. Snapshot the live angle into an
      // inline transform, drop the keyframe, then nudge to angle + 360°
      // so the existing `transition: transform` decelerates forward
      // instead of snapping or reversing.
      const t = getComputedStyle(logo).transform;
      let angle = 0;
      if (t && t !== "none") {
        const m = new DOMMatrix(t);
        angle = (Math.atan2(m.b, m.a) * 180) / Math.PI;
        if (angle < 0) angle += 360;
      }
      logo.style.transform = `rotate(${angle}deg)`;
      logo.classList.remove("animate-spin");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          logo.style.transform = "rotate(360deg)";
        });
      });
    }
  }
};

// Favicon spinner. The browser re-decodes the icon URL on every change, so
// the loop draws a rotated frame onto an offscreen canvas and pushes its
// data-URI as the icon href. ~24fps is enough to look smooth without
// drowning the tab in PNG decodes.
const FAVICON_SIZE = 64;
let faviconRAF = 0;
let faviconStart = 0;
let faviconCanvas: HTMLCanvasElement | null = null;
let faviconImage: HTMLImageElement | null = null;
let faviconLink: HTMLLinkElement | null = null;
let faviconOriginalHref = "";

const pickFaviconLink = (): HTMLLinkElement | null => {
  // Prefer the largest registered favicon size as the source — gives the
  // crispest input for the canvas rotation.
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'),
  );
  let best: HTMLLinkElement | null = null;
  let bestSize = -1;
  for (const link of links) {
    // `link.sizes` is a DOMTokenList in spec-compliant browsers, but
    // some hosts (jsdom, jdom-on-mobile) leave it undefined when no
    // attribute is set. Read the raw attribute as the source of truth
    // and treat absence as size 0 — the existing `||` already coerces
    // NaN/0 to 0, so the comparison falls back to "first link wins."
    const raw = link.getAttribute("sizes") ?? "";
    const sz = parseInt(raw.split("x")[0] ?? "0", 10) || 0;
    if (sz > bestSize) {
      best = link;
      bestSize = sz;
    }
  }
  return best ?? links[0] ?? null;
};

const spinFavicon = (on: boolean): void => {
  if (on) {
    if (faviconRAF) return;
    faviconLink = pickFaviconLink();
    if (!faviconLink) return;
    faviconOriginalHref = faviconLink.href;
    faviconImage = new Image();
    faviconImage.crossOrigin = "anonymous";
    faviconImage.src = faviconOriginalHref;
    faviconCanvas = document.createElement("canvas");
    faviconCanvas.width = FAVICON_SIZE;
    faviconCanvas.height = FAVICON_SIZE;
    faviconStart = performance.now();
    let lastFrame = 0;
    const tick = (t: number): void => {
      // ~24fps cap — favicon decoding is the bottleneck, not draw time
      if (t - lastFrame >= 1000 / 24) {
        lastFrame = t;
        const ctx = faviconCanvas!.getContext("2d");
        const img = faviconImage!;
        const link = faviconLink;
        if (ctx && img.complete && img.naturalWidth > 0 && link) {
          const angle = ((t - faviconStart) / 1000) * Math.PI * 2;
          ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);
          ctx.save();
          ctx.translate(FAVICON_SIZE / 2, FAVICON_SIZE / 2);
          ctx.rotate(angle);
          ctx.drawImage(
            img,
            -FAVICON_SIZE / 2,
            -FAVICON_SIZE / 2,
            FAVICON_SIZE,
            FAVICON_SIZE,
          );
          ctx.restore();
          link.href = faviconCanvas!.toDataURL("image/png");
        }
      }
      faviconRAF = requestAnimationFrame(tick);
    };
    faviconRAF = requestAnimationFrame(tick);
  } else {
    if (!faviconRAF) return;
    cancelAnimationFrame(faviconRAF);
    faviconRAF = 0;
    if (faviconLink && faviconOriginalHref) {
      faviconLink.href = faviconOriginalHref;
    }
    faviconCanvas = null;
    faviconImage = null;
  }
};

window.addEventListener("beforeunload", () => {
  unloading = true;
  spinLogo(true);
  spinFavicon(true);
});
window.addEventListener("pageshow", () => {
  unloading = false;
  spinLogo(false);
  spinFavicon(false);
});
// Backstop for bfcache restores: LiveView's pageshow handler may force a
// reconnect that bypasses `phx-update="ignore"` on the first morph, leaving
// the keyframe re-applied with no follow-up signal. The page-loading-stop
// event fires after every LiveView lifecycle transition (including the
// post-bfcache reconnect), so we use it to re-assert the rest state.
window.addEventListener("phx:page-loading-start", () => {
  if (unloading) return;
  spinLogo(true);
  spinFavicon(true);
});
window.addEventListener("phx:page-loading-stop", () => {
  if (unloading) return;
  spinLogo(false);
  // Both spinners started on page-loading-start must unwind here: the
  // favicon loop used to be left out, so its rAF tick (canvas redraw +
  // toDataURL at ~24fps) kept running forever after any LiveView
  // navigation transition.
  spinFavicon(false);
  // Any successful LV transition means the channel is talking again. If
  // the disconnect UI was shown because LV's reloadWithJitter fallback
  // fired (overridden in app.ts) while the raw socket stayed "alive,"
  // onOpen never fires to dismiss the toast — this is the all-clear.
  if (document.getElementById(DISCONNECT_ID) || pendingShow !== null) {
    hideDisconnectToast();
  }
});

// Grace period before the dramatic disconnect overlay kicks in. Phoenix
// LiveView reconnects on its own (heartbeat misses, brief network blips,
// devserver hot-reload) within a couple of seconds; firing the full
// fallback every time the socket sneezes is jarring. Wait this long for
// `onOpen` to cancel us before committing to the animation.
const DISCONNECT_GRACE_MS = 2000;
let pendingShow: number | null = null;

const revealDisconnectUI = (): void => {
  if (document.getElementById(DISCONNECT_ID)) return;

  showDisconnectFallback();

  const container = document.querySelector("[id$='toast-container']");
  if (!container) return;
  const toast = document.createElement("div");
  toast.id = DISCONNECT_ID;
  toast.className =
    "pointer-events-auto flex items-center gap-3 rounded border border-red/30 bg-base-dark px-4 py-3";
  // Mirrors the Elixir <.icon name={:warning} class="text-red"> output —
  // see lib/operation_web/components/ui/icon.ex (\uf071 is the warning glyph).
  const icon = document.createElement("icon");
  icon.className = "nf-icon flex-shrink-0 text-red";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "\uf071";
  const msg = document.createElement("span");
  msg.className = "text-sm text-text";
  msg.textContent = "Connection lost. ";
  const btn = document.createElement("button");
  btn.className = "text-accent underline";
  btn.textContent = "Refresh";
  btn.addEventListener("click", () => {
    location.reload();
  });
  msg.appendChild(btn);
  toast.appendChild(icon);
  toast.appendChild(msg);
  container.appendChild(toast);
};

export const showDisconnectToast = (): void => {
  if (unloading || expectedShutdown) {
    spinLogo(true);
    spinFavicon(true);
    return;
  }
  if (pendingShow !== null) return;
  if (document.getElementById(DISCONNECT_ID)) return;
  pendingShow = window.setTimeout(() => {
    pendingShow = null;
    revealDisconnectUI();
  }, DISCONNECT_GRACE_MS);
};

export const hideDisconnectToast = (): void => {
  if (pendingShow !== null) {
    clearTimeout(pendingShow);
    pendingShow = null;
  }
  expectedShutdown = false;
  spinLogo(false);
  spinFavicon(false);
  const el = document.getElementById(DISCONNECT_ID);
  if (el) el.remove();
  hideDisconnectFallback();
};
